import { UseFormReturn } from "react-hook-form";
import { ZodSchema } from "zod";
import { useSignupMutation } from "./useSignupMutation";
import { useGoogleSignupMutation } from "./useGoogleSignupMutation";
import { useTrackSignupEvents } from "./useTrackSignupEvents";
import { signupSchemaWithPhone } from "../auth.schemas";
import { Invitation, ClaimedVia } from "@core/features/invitation";

export type SignupFormData = {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
  confirmPhoneNumber?: string;
};

export type UseSignupProps = {
  /** Invitation data */
  invitation: Invitation;
  /** How the invitation was claimed */
  claimedVia?: ClaimedVia | null;
  /** Success callback */
  onSuccess?: (data: any) => void;
  /** Error callback */
  onError?: () => void;
  /** Custom schema (defaults to baseSignupSchema) */
  schema?: ZodSchema;
};

export type UseSignupReturn = {
  /** Signup mutation for form submission */
  signupMutation: ReturnType<typeof useSignupMutation>;
  /** Schema for form validation */
  schema: ZodSchema;
  /** Default values for form */
  defaultValues: SignupFormData;
  /** Invitation data (for tracking) */
  invitation: Invitation;
};

/**
 * Composite hook for signup functionality.
 * Combines signup mutation and analytics tracking.
 *
 * Usage:
 * ```tsx
 * const { signupMutation, schema, defaultValues, invitation } = useSignup({
 *   invitation,
 *   onSuccess: (data) => navigate('/welcome'),
 *   onError: () => toast.error('Signup failed'),
 * });
 *
 * const { form, handleSubmit } = useForm({
 *   schema,
 *   onSubmit: signupMutation.mutateAsync,
 *   defaultValues,
 * });
 *
 * // Set up tracking after form is available
 * useSignupTracking({ invitation, watch: form.watch });
 *
 * // Set up Google signup after form is available
 * const googleSignupMutation = useGoogleSignup({ form, invitation, onSuccess, onError });
 * ```
 */
export function useSignup({
  invitation,
  claimedVia,
  onSuccess,
  onError,
  schema = signupSchemaWithPhone,
}: UseSignupProps): UseSignupReturn {
  // Email/password signup mutation
  const signupMutation = useSignupMutation({
    invitation,
    claimedVia,
    onSuccess,
    onError,
    schema,
  });

  const defaultValues: SignupFormData = {
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    confirmPhoneNumber: "",
  };

  return {
    signupMutation,
    schema,
    defaultValues,
    invitation,
  };
}

/**
 * Hook for signup tracking - requires form watch function.
 * Call this after useForm to track signup events.
 */
export function useSignupTracking(props: {
  invitation: Invitation;
  watch: UseFormReturn<any>["watch"];
}) {
  useTrackSignupEvents({
    invitation: props.invitation,
    watch: props.watch,
  });
}

/**
 * Hook for Google signup - requires form instance.
 * Call this after useForm to get Google signup functionality.
 */
export function useGoogleSignup(props: {
  form: UseFormReturn<any>;
  invitation: Invitation;
  claimedVia?: ClaimedVia | null;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  schema?: ZodSchema;
}) {
  return useGoogleSignupMutation({
    form: props.form,
    invitation: props.invitation,
    claimedVia: props.claimedVia,
    onSuccess: props.onSuccess,
    onError: props.onError,
    schema: props.schema || signupSchemaWithPhone,
  });
}

export default useSignup;
